/**
 * Gate 2 Phase 3 — SafeEdit lifecycle projection (computed, not a second truth store).
 * Resolves authoritative GenerationRun + active head + audit signals into client/studio phases.
 */
import { ClientSafePatchCompilationPhaseSchema, } from "../contracts/client-safe-patch-outcome.js";
import { z } from "zod";
export const SafeEditLifecycleStateSchema = z.enum([
    "REJECTED",
    "ACCEPTED_PENDING",
    "QUEUED",
    "GENERATING",
    "QA_PENDING",
    "QA_BLOCKED",
    "CONTRADICTION_BLOCKED",
    "ACTIVATION_REJECTED",
    "FAILED",
    "SUPERSEDED",
    "ACTIVE",
]);
function hasAudit(types, needle) {
    return (types ?? []).some((t) => t === needle);
}
/**
 * Map authoritative run/head/audit state → portal compilationPhase + lifecycle.
 * Terminal success requires toRevisionId === currentActiveRevisionId.
 */
export function resolveSafeEditLifecycle(input) {
    if (input.attemptStatus === "REJECTED") {
        return {
            lifecycle: "REJECTED",
            compilationPhase: ClientSafePatchCompilationPhaseSchema.enum.NONE,
            isTerminalSuccess: false,
            isTerminalFailure: true,
            clientSafeReason: "This update could not be accepted.",
        };
    }
    const run = input.runStatus;
    if (!run) {
        return {
            lifecycle: "ACCEPTED_PENDING",
            compilationPhase: "COMPILATION_PENDING",
            isTerminalSuccess: false,
            isTerminalFailure: false,
            clientSafeReason: "Your update was submitted and is waiting to process. It is not live yet.",
        };
    }
    if (run === "SUPERSEDED") {
        return {
            lifecycle: "SUPERSEDED",
            compilationPhase: "SUPERSEDED",
            isTerminalSuccess: false,
            isTerminalFailure: true,
            clientSafeReason: "A newer update replaced this one before it finished.",
        };
    }
    if (run === "QA_FAILED") {
        return {
            lifecycle: "QA_BLOCKED",
            compilationPhase: "QA_BLOCKED",
            isTerminalSuccess: false,
            isTerminalFailure: true,
            clientSafeReason: "Quality checks blocked this update. Contact your agency.",
        };
    }
    if (run === "FAILED" || run === "CANCELLED") {
        if (hasAudit(input.recentAuditEventTypes, "businessgraph_contradiction_blocked") ||
            hasAudit(input.recentAuditEventTypes, "businessgraph_contradiction_input_invalid")) {
            return {
                lifecycle: "CONTRADICTION_BLOCKED",
                compilationPhase: "CONTRADICTION_BLOCKED",
                isTerminalSuccess: false,
                isTerminalFailure: true,
                clientSafeReason: "Conflicting business details blocked this update. Contact your agency to resolve.",
            };
        }
        if (hasAudit(input.recentAuditEventTypes, "businessgraph_activation_rejected")) {
            return {
                lifecycle: "ACTIVATION_REJECTED",
                compilationPhase: "ACTIVATION_REJECTED",
                isTerminalSuccess: false,
                isTerminalFailure: true,
                clientSafeReason: "This update could not be activated. Contact your agency.",
            };
        }
        return {
            lifecycle: "FAILED",
            compilationPhase: "FAILED",
            isTerminalSuccess: false,
            isTerminalFailure: true,
            clientSafeReason: "Processing failed. Contact your agency for help.",
        };
    }
    if (run === "QA_PASSED" ||
        run === "QA_PASSED_WITH_WARNINGS" ||
        run === "EXPORT_READY" ||
        run === "EXPORTING" ||
        run === "EXPORTED") {
        const active = input.toRevisionId != null &&
            input.currentActiveRevisionId != null &&
            input.toRevisionId === input.currentActiveRevisionId;
        if (active) {
            return {
                lifecycle: "ACTIVE",
                compilationPhase: "READY",
                isTerminalSuccess: true,
                isTerminalFailure: false,
                clientSafeReason: "Your update is now active on your preview site.",
            };
        }
        return {
            lifecycle: "ACTIVATION_REJECTED",
            compilationPhase: "ACTIVATION_REJECTED",
            isTerminalSuccess: false,
            isTerminalFailure: true,
            clientSafeReason: "This update did not become the active website version.",
        };
    }
    if (run === "GENERATED" || run === "QA_QUEUED" || run === "QA_RUNNING") {
        return {
            lifecycle: "QA_PENDING",
            compilationPhase: "QA_PENDING",
            isTerminalSuccess: false,
            isTerminalFailure: false,
            clientSafeReason: "Quality checks are running on your update. It is not live yet.",
        };
    }
    if (run === "PENDING" || run === "GENERATING") {
        return {
            lifecycle: run === "PENDING" ? "QUEUED" : "GENERATING",
            compilationPhase: "COMPILATION_PENDING",
            isTerminalSuccess: false,
            isTerminalFailure: false,
            clientSafeReason: "Your update is processing. It is not live yet.",
        };
    }
    return {
        lifecycle: "ACCEPTED_PENDING",
        compilationPhase: "COMPILATION_PENDING",
        isTerminalSuccess: false,
        isTerminalFailure: false,
        clientSafeReason: "Your update is processing. It is not live yet.",
    };
}
//# sourceMappingURL=resolve-safe-edit-lifecycle.js.map